import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/local.js';
import type { LocalPlan } from '../db/local.js';
import { activatePlan } from '../db/plans.js';
import { readProfile, updateModules } from '../db/profile.js';
import { activeModules } from '@kraftwerk/shared';
import type { Module } from '@kraftwerk/shared';
import { ModulesSetting } from './ModulesSetting.js';
import { useModules } from '../hooks/useModules.js';
import {
  canPromptInstall,
  promptInstall,
  readSetupVisibility,
  setupHiddenFrom,
  setupStatus,
  writeSetupHidden,
} from '../onboarding.js';
import type { SetupStatus, SetupStep } from '../onboarding.js';
import { isStandalone } from '../push/usePush.js';
import type { PushState } from '../push/usePush.js';
import { EnergySetting } from './EnergySetting.js';
import { PushSetup } from './PushSetup.js';
import { TrainingDays } from './training/TrainingDays.js';
import { useSwipeBack } from '../gestures/SwipeProvider.js';

/** Wohin „Mehr einrichten" führt — die App-Hülle kennt die Wege. */
export type SetupTarget = 'supplements' | 'household' | 'calendar' | 'plans';
export type SetupPage = SetupStep | 'more';

/**
 * Stand der Einrichtung, live aus den Daten. `null`, solange er lädt — dann
 * zeigt die Kachel nichts, statt kurz „0 von 4" zu behaupten.
 */
export function useSetupStatus(push: PushState): SetupStatus | null {
  const facts = useLiveQuery(async () => {
    const [profile, plans, slots] = await Promise.all([
      readProfile(db),
      db.training_plans.toArray(),
      db.training_slots.toArray(),
    ]);
    return {
      modulesChosen: profile.modules !== null,
      modules: activeModules(profile.modules),
      hasTargets: profile.targets !== null,
      hasActivePlan: plans.some((plan) => !plan.deletedAt && plan.active),
      hasTrainingDays: slots.some((slot) => !slot.deletedAt && slot.active),
    };
  }, []);
  if (!facts) return null;
  return setupStatus({ ...facts, standalone: isStandalone(), pushStatus: push.status });
}

export function useSetupHidden(): boolean | null {
  const facts = useLiveQuery(() => readSetupVisibility(db), []);
  const hidden = facts ? setupHiddenFrom(facts) : null;
  // Die einmalige Entscheidung festhalten — außerhalb der Live-Abfrage, die
  // nicht schreiben darf.
  useEffect(() => {
    if (facts && facts.stored === undefined && hidden !== null) {
      void writeSetupHidden(hidden, db);
    }
  }, [facts, hidden]);
  return hidden;
}

/**
 * Auf „Heute", bis alles eingerichtet oder die Kachel ausgeblendet ist. Sie
 * steht oben, weil ohne sie die übrigen Kacheln leer sind.
 */
export function SetupTile({
  status,
  onOpen,
}: {
  status: SetupStatus;
  onOpen: (page: SetupPage) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <section
      aria-labelledby="setup-tile-heading"
      className="kw-tile flex flex-col gap-3 border-accent"
    >
      <div>
        <p className="kw-kicker">
          {t('setup.progress', { done: status.doneCount, total: status.steps.length })}
        </p>
        <h2 id="setup-tile-heading" className="mt-1 font-display text-xl font-bold">
          {t('setup.tileHeading')}
        </h2>
        <p className="mt-1 text-sm text-ink-muted">{t('setup.tileIntro')}</p>
      </div>
      <ol className="flex flex-col gap-1">
        {status.steps.map((entry, index) => (
          <li key={entry.step}>
            <button
              type="button"
              onClick={() => onOpen(entry.step)}
              className="flex min-h-11 w-full items-center gap-3 rounded-xl px-1 text-left"
            >
              <StepMark done={entry.done} number={index + 1} />
              <span className={entry.done ? 'text-ink-muted line-through' : ''}>
                {t(`setup.step.${entry.step}.short`)}
              </span>
              <span className="sr-only">
                {entry.done ? t('setup.doneLabel') : t('setup.openLabel')}
              </span>
            </button>
          </li>
        ))}
      </ol>
      {status.next ? (
        <button type="button" onClick={() => onOpen(status.next!)} className="kw-btn">
          {t('setup.continue', { step: t(`setup.step.${status.next}.short`) })}
        </button>
      ) : null}
      <button
        type="button"
        onClick={() => void writeSetupHidden(true, db)}
        aria-describedby="setup-hide-hint"
        className="kw-btn kw-btn-quiet kw-btn-small"
      >
        {t('setup.hide')}
      </button>
      <p id="setup-hide-hint" className="-mt-2 text-center text-xs text-ink-muted">
        {t('setup.hideHint')}
      </p>
    </section>
  );
}

function StepMark({ done, number }: { done: boolean; number: number }): React.JSX.Element {
  return (
    <span
      aria-hidden="true"
      className={`flex size-7 shrink-0 items-center justify-center rounded-full border text-sm font-semibold ${
        done ? 'border-good bg-good text-ground' : 'border-line text-ink-muted'
      }`}
    >
      {done ? '✓' : number}
    </span>
  );
}

/**
 * Die Einrichtung, Schritt für Schritt. Jeder Schritt benutzt dieselben
 * Bausteine wie die Einstellungen — es gibt keinen zweiten Weg, Ziele oder
 * Trainingstage zu setzen, nur einen zweiten Eingang.
 */
export function SetupScreen({
  page,
  status,
  push,
  onPage,
  onClose,
  onNavigate,
}: {
  page: SetupPage;
  status: SetupStatus;
  push: PushState;
  onPage: (page: SetupPage) => void;
  onClose: () => void;
  onNavigate: (target: SetupTarget) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  // Eine Unteransicht: vom linken Rand wischen schließt sie (24.10.2026).
  useSwipeBack(onClose);
  const heading = useRef<HTMLHeadingElement>(null);
  const pages: SetupPage[] = [...status.steps.map((entry) => entry.step), 'more'];
  const index = Math.max(0, pages.indexOf(page));
  const done = page !== 'more' && status.steps.find((entry) => entry.step === page)?.done;

  // Beim Blättern liest der Bildschirmleser die neue Überschrift vor.
  useEffect(() => heading.current?.focus(), [page]);

  return (
    <section aria-labelledby="setup-heading" className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <p className="kw-kicker">
          {page === 'more'
            ? t('setup.optional')
            : t('setup.stepOf', { step: index + 1, total: status.steps.length })}
        </p>
        <button type="button" onClick={onClose} className="kw-btn kw-btn-quiet kw-btn-small px-3">
          {t('settings.close')}
        </button>
      </div>
      <div>
        <h2
          id="setup-heading"
          ref={heading}
          tabIndex={-1}
          className="font-display text-2xl font-bold uppercase"
        >
          {t(`setup.step.${page}.title`)}
        </h2>
        <p className="mt-1 text-sm text-ink-muted">{t(`setup.step.${page}.why`)}</p>
        {done ? (
          <p role="status" className="mt-2 text-sm font-semibold text-good">
            {t('setup.stepDone')}
          </p>
        ) : null}
      </div>

      {page === 'modules' ? <ModulesStep /> : null}
      {page === 'install' ? <InstallStep /> : null}
      {page === 'push' ? <PushSetup state={push} /> : null}
      {page === 'body' ? <EnergySetting /> : null}
      {page === 'training' ? <TrainingStep onNavigate={onNavigate} /> : null}
      {page === 'more' ? <MoreStep onNavigate={onNavigate} /> : null}

      <div className="flex gap-2">
        {index > 0 ? (
          <button
            type="button"
            onClick={() => onPage(pages[index - 1]!)}
            className="kw-btn kw-btn-quiet flex-1"
          >
            {t('setup.back')}
          </button>
        ) : null}
        {page === 'more' ? (
          <button type="button" onClick={onClose} className="kw-btn flex-1">
            {t('setup.finish')}
          </button>
        ) : (
          // Bernstein heißt „nächste Handlung“ — Überspringen ist keine.
          <button
            type="button"
            onClick={() => onPage(pages[index + 1]!)}
            className={`kw-btn flex-1 ${done ? '' : 'kw-btn-quiet'}`}
          >
            {done ? t('setup.next') : t('setup.skip')}
          </button>
        )}
      </div>
    </section>
  );
}

function InstallStep(): React.JSX.Element {
  const { t } = useTranslation();
  const [offer] = useState(canPromptInstall);

  if (isStandalone()) {
    return <p className="kw-tile text-sm">{t('setup.install.already')}</p>;
  }
  return (
    <div className="kw-tile flex flex-col gap-3">
      {offer ? (
        <button type="button" onClick={() => void promptInstall()} className="kw-btn">
          {t('setup.install.button')}
        </button>
      ) : (
        <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm">
          <li>{t('setup.install.ios1')}</li>
          <li>{t('setup.install.ios2')}</li>
          <li>{t('setup.install.ios3')}</li>
        </ol>
      )}
      <p className="text-xs text-ink-muted">{t('setup.install.relogin')}</p>
    </div>
  );
}

function TrainingStep({
  onNavigate,
}: {
  onNavigate: (target: SetupTarget) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const plans = useLiveQuery(
    async () =>
      (await db.training_plans.toArray())
        .filter((plan) => !plan.deletedAt)
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [],
    [] as LocalPlan[],
  );

  return (
    <>
      <fieldset className="kw-tile flex flex-col gap-2">
        <legend className="kw-kicker float-left mb-1 w-full">{t('setup.training.plan')}</legend>
        {plans.length === 0 ? (
          <p className="text-sm text-ink-muted">{t('setup.training.noPlans')}</p>
        ) : (
          plans.map((plan) => (
            <button
              key={plan.id}
              type="button"
              aria-pressed={plan.active}
              onClick={() => void activatePlan(plan.id, db)}
              className={`flex min-h-14 flex-col items-start justify-center rounded-xl border px-3 py-2 text-left ${
                plan.active ? 'border-accent' : 'border-line'
              }`}
            >
              <span className="text-sm font-medium">{plan.name}</span>
              {plan.note ? <span className="text-xs text-ink-muted">{plan.note}</span> : null}
            </button>
          ))
        )}
        <button
          type="button"
          onClick={() => onNavigate('plans')}
          className="kw-btn kw-btn-quiet kw-btn-small"
        >
          {t('setup.training.own')}
        </button>
      </fieldset>
      <TrainingDays />
    </>
  );
}

/** Module wählen — „So übernehmen“ hält die Wahl fest, auch wenn alles an bleibt. */
function ModulesStep(): React.JSX.Element {
  const { t } = useTranslation();
  const modules = useModules();
  return (
    <div className="kw-tile flex flex-col gap-3">
      <ModulesSetting framed={false} />
      <button
        type="button"
        onClick={() => void updateModules(modules, db)}
        className="kw-btn kw-btn-small"
      >
        {t('modules.confirm')}
      </button>
    </div>
  );
}

function MoreStep({
  onNavigate,
}: {
  onNavigate: (target: SetupTarget) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const modules = useModules();
  // Nur, was zu den gewählten Modulen gehört.
  const needs: Record<Exclude<SetupTarget, 'plans'>, Module[]> = {
    supplements: ['nutrition'],
    household: ['household'],
    calendar: ['planning'],
  };
  const targets = (['supplements', 'household', 'calendar'] as const).filter((target) =>
    needs[target].some((module) => modules.includes(module)),
  );
  return (
    <ul className="flex flex-col gap-2">
      {targets.map((target) => (
        <li key={target} className="kw-tile flex flex-col gap-2">
          <h3 className="font-medium">{t(`setup.more.${target}.title`)}</h3>
          <p className="text-sm text-ink-muted">{t(`setup.more.${target}.why`)}</p>
          <button
            type="button"
            onClick={() => onNavigate(target)}
            className="kw-btn kw-btn-quiet kw-btn-small"
          >
            {t(`setup.more.${target}.action`)}
          </button>
        </li>
      ))}
    </ul>
  );
}
